import { StorageHelper } from './helpers.js';

const SUPPORTED_FIELD_TYPES = new Set(['text', 'email', 'tel', 'textarea']);
const MAX_FIELD_VALUE_LENGTH = 1000;
const MAX_FIELDS = 10;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const PHONE_REGEX = /^[+]?[\d\s().-]{6,30}$/;
const HONEYPOT_NAME = 'website_url';
const SHAKE_DURATION_MS = 360;
const CHAR_COUNT_WARN_RATIO = 0.8;
const SUCCESS_CARD_DELAY_MS = 450;

const INPUTMODE_BY_TYPE = {
  email: 'email',
  tel: 'tel'
};

function draftStorageKey(botId, formId) {
  return `chatyx_lead_draft:${botId || 'default'}:${formId || 'lead_capture'}`;
}

function normalizeSchema(rawSchema) {
  if (!rawSchema || typeof rawSchema !== 'object') return null;
  const fields = Array.isArray(rawSchema.fields) ? rawSchema.fields : [];
  const normalizedFields = fields
    .filter((f) => f && typeof f === 'object' && typeof f.name === 'string')
    .slice(0, MAX_FIELDS)
    .map((f) => ({
      name: String(f.name).trim().slice(0, 64),
      label: typeof f.label === 'string' ? f.label.slice(0, 120) : f.name,
      type: SUPPORTED_FIELD_TYPES.has(f.type) ? f.type : 'text',
      required: f.required === true,
      placeholder: typeof f.placeholder === 'string' ? f.placeholder.slice(0, 120) : '',
      maxLength:
        typeof f.maxLength === 'number' && f.maxLength > 0
          ? Math.min(MAX_FIELD_VALUE_LENGTH, Math.floor(f.maxLength))
          : MAX_FIELD_VALUE_LENGTH,
      autocomplete: typeof f.autocomplete === 'string' ? f.autocomplete : undefined
    }))
    .filter((f) => f.name.length > 0);

  if (normalizedFields.length === 0) return null;

  const consent =
    rawSchema.consent && typeof rawSchema.consent === 'object'
      ? {
          label: typeof rawSchema.consent.label === 'string' ? rawSchema.consent.label : '',
          required: rawSchema.consent.required !== false,
          text: typeof rawSchema.consent.text === 'string' ? rawSchema.consent.text : ''
        }
      : null;

  return {
    id: typeof rawSchema.id === 'string' ? rawSchema.id : 'lead_capture',
    version: typeof rawSchema.version === 'number' ? rawSchema.version : 1,
    title: typeof rawSchema.title === 'string' ? rawSchema.title : '',
    description: typeof rawSchema.description === 'string' ? rawSchema.description : '',
    submitLabel: typeof rawSchema.submitLabel === 'string' ? rawSchema.submitLabel : '',
    fields: normalizedFields,
    consent
  };
}

function validateSingleField(field, rawValue) {
  const value = typeof rawValue === 'string' ? rawValue.trim() : '';
  if (field.required && !value) return 'required';
  if (!value) return null;
  if (value.length > field.maxLength) return 'tooLong';
  if (field.type === 'email' && !EMAIL_REGEX.test(value)) return 'invalidEmail';
  if (field.type === 'tel' && !PHONE_REGEX.test(value)) return 'invalidPhone';
  return null;
}

function validateValues(schema, values) {
  const errors = {};
  for (const field of schema.fields) {
    const code = validateSingleField(field, values[field.name]);
    if (code) errors[field.name] = code;
  }
  return { valid: Object.keys(errors).length === 0, errors };
}

function buildSummary(schema, values, t) {
  const parts = [];
  for (const field of schema.fields) {
    const value = values[field.name];
    if (typeof value === 'string' && value.trim()) {
      parts.push(`${field.label}: ${value.trim()}`);
    }
  }
  if (parts.length === 0) return t.leadFormSubmitted || 'Form submitted';
  return parts.join(' · ');
}

function createCheckIcon() {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('viewBox', '0 0 24 24');
  svg.setAttribute('aria-hidden', 'true');
  svg.setAttribute('class', 'chatyx-lead-form__check-icon');
  const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  path.setAttribute('d', 'M5 12.5l4.5 4.5L19 7.5');
  path.setAttribute('fill', 'none');
  path.setAttribute('stroke', 'currentColor');
  path.setAttribute('stroke-width', '2.8');
  path.setAttribute('stroke-linecap', 'round');
  path.setAttribute('stroke-linejoin', 'round');
  svg.appendChild(path);
  return svg;
}

function buildLabel(field, inputId, requiredHint) {
  const labelEl = document.createElement('label');
  labelEl.setAttribute('for', inputId);
  labelEl.className = 'chatyx-lead-form__label';
  labelEl.appendChild(document.createTextNode(field.label));
  if (field.required) {
    const star = document.createElement('span');
    star.className = 'chatyx-lead-form__required';
    star.setAttribute('aria-hidden', 'true');
    star.textContent = '*';
    labelEl.appendChild(star);
    if (requiredHint) {
      const sr = document.createElement('span');
      sr.className = 'chatyx-lead-form__sr-only';
      sr.textContent = ` (${requiredHint})`;
      labelEl.appendChild(sr);
    }
  }
  return labelEl;
}

export function renderLeadForm({ schema: rawSchema, container, t, botId, onSubmit, onStart }) {
  const schema = normalizeSchema(rawSchema);
  if (!schema) return null;

  const draftKey = draftStorageKey(botId, schema.id);
  const draft = StorageHelper.get(draftKey) || {};

  const wrapper = document.createElement('form');
  wrapper.className = 'chatyx-lead-form';
  wrapper.setAttribute('novalidate', 'true');
  wrapper.setAttribute('data-form-id', schema.id);
  wrapper.setAttribute('aria-label', schema.title || t.leadFormTitle || 'Contact form');

  // Fire `onStart` once, the first time the visitor interacts with any field.
  if (typeof onStart === 'function') {
    const onceStart = () => {
      wrapper.removeEventListener('input', onceStart);
      try {
        onStart();
      } catch (_) {
        /* telemetry must never break the form */
      }
    };
    wrapper.addEventListener('input', onceStart);
  }

  if (schema.title) {
    const title = document.createElement('div');
    title.className = 'chatyx-lead-form__title';
    title.textContent = schema.title;
    wrapper.appendChild(title);
  }
  if (schema.description) {
    const desc = document.createElement('div');
    desc.className = 'chatyx-lead-form__description';
    desc.textContent = schema.description;
    wrapper.appendChild(desc);
  }

  const fieldset = document.createElement('fieldset');
  fieldset.className = 'chatyx-lead-form__fieldset';
  const legend = document.createElement('legend');
  legend.className = 'chatyx-lead-form__sr-only';
  legend.textContent = schema.title || t.leadFormFieldset || t.leadFormTitle || 'Form';
  fieldset.appendChild(legend);
  wrapper.appendChild(fieldset);

  const inputsByName = new Map();
  const errorsByName = new Map();
  const rowsByName = new Map();
  const countersByName = new Map();

  schema.fields.forEach((field, fieldIndex) => {
    const fieldRow = document.createElement('div');
    fieldRow.className = 'chatyx-lead-form__field';
    fieldRow.dataset.fieldType = field.type;

    const inputId = `chatyx-lead-${schema.id}-${field.name}`;
    const labelEl = buildLabel(field, inputId, t.leadFormRequiredHint);

    const input = field.type === 'textarea' ? document.createElement('textarea') : document.createElement('input');
    input.id = inputId;
    input.name = field.name;
    input.className = 'chatyx-lead-form__input';
    input.setAttribute('maxlength', String(field.maxLength));
    if (field.required) input.setAttribute('aria-required', 'true');
    if (field.placeholder) input.setAttribute('placeholder', field.placeholder);
    if (field.autocomplete) input.setAttribute('autocomplete', field.autocomplete);
    if (field.type !== 'textarea') input.setAttribute('type', field.type);
    if (field.type === 'textarea') input.setAttribute('rows', '3');

    const inputmode = INPUTMODE_BY_TYPE[field.type];
    if (inputmode) input.setAttribute('inputmode', inputmode);
    if (field.type !== 'textarea') {
      const isLast = fieldIndex === schema.fields.length - 1;
      input.setAttribute('enterkeyhint', isLast ? 'send' : 'next');
    }

    if (typeof draft[field.name] === 'string') input.value = draft[field.name];

    const errorEl = document.createElement('div');
    errorEl.className = 'chatyx-lead-form__error';
    errorEl.id = `${inputId}-error`;
    errorEl.setAttribute('aria-live', 'polite');
    input.setAttribute('aria-describedby', errorEl.id);

    fieldRow.appendChild(labelEl);
    fieldRow.appendChild(input);
    fieldRow.appendChild(errorEl);

    if (field.type === 'textarea') {
      const counter = document.createElement('div');
      counter.className = 'chatyx-lead-form__char-count';
      counter.setAttribute('aria-hidden', 'true');
      fieldRow.appendChild(counter);
      countersByName.set(field.name, counter);
    }

    fieldset.appendChild(fieldRow);

    inputsByName.set(field.name, input);
    errorsByName.set(field.name, errorEl);
    rowsByName.set(field.name, fieldRow);
  });

  // Honeypot — visually hidden, ignored by humans.
  const honeypot = document.createElement('input');
  honeypot.type = 'text';
  honeypot.name = HONEYPOT_NAME;
  honeypot.tabIndex = -1;
  honeypot.autocomplete = 'off';
  honeypot.setAttribute('aria-hidden', 'true');
  honeypot.className = 'chatyx-lead-form__honeypot';
  wrapper.appendChild(honeypot);

  let consentInput = null;
  if (schema.consent) {
    const consentRow = document.createElement('div');
    consentRow.className = 'chatyx-lead-form__consent';
    consentInput = document.createElement('input');
    consentInput.type = 'checkbox';
    consentInput.id = `chatyx-lead-${schema.id}-consent`;
    if (schema.consent.required) consentInput.setAttribute('aria-required', 'true');
    const consentLabel = document.createElement('label');
    consentLabel.setAttribute('for', consentInput.id);
    consentLabel.textContent = schema.consent.label || t.leadFormConsentDefault || 'I accept';
    consentRow.appendChild(consentInput);
    consentRow.appendChild(consentLabel);
    wrapper.appendChild(consentRow);
  }

  const actions = document.createElement('div');
  actions.className = 'chatyx-lead-form__actions';
  const submitBtn = document.createElement('button');
  submitBtn.type = 'submit';
  submitBtn.className = 'chatyx-lead-form__submit';
  const submitLabelEl = document.createElement('span');
  submitLabelEl.className = 'chatyx-lead-form__submit-label';
  submitLabelEl.textContent = schema.submitLabel || t.leadFormSubmit || 'Send';
  submitBtn.appendChild(submitLabelEl);
  if (schema.consent && schema.consent.required) submitBtn.disabled = true;
  actions.appendChild(submitBtn);
  wrapper.appendChild(actions);

  const status = document.createElement('div');
  status.className = 'chatyx-lead-form__status';
  status.setAttribute('role', 'status');
  status.setAttribute('aria-live', 'polite');
  wrapper.appendChild(status);

  if (consentInput) {
    consentInput.addEventListener('change', () => {
      submitBtn.disabled = schema.consent.required && !consentInput.checked;
    });
  }

  const persistDraft = () => {
    const snapshot = {};
    for (const [name, input] of inputsByName) {
      snapshot[name] = input.value;
    }
    StorageHelper.set(draftKey, snapshot);
  };

  const lookupErrorMessage = (key) => {
    const map = {
      required: t.leadFormErrorRequired || 'This field is required',
      tooLong: t.leadFormErrorTooLong || 'Value too long',
      invalidEmail: t.leadFormErrorEmail || 'Invalid email',
      invalidPhone: t.leadFormErrorPhone || 'Invalid phone'
    };
    return map[key] || t.leadFormErrorGeneric || 'Invalid value';
  };

  const setFieldError = (name, code) => {
    const errorEl = errorsByName.get(name);
    const input = inputsByName.get(name);
    if (!errorEl || !input) return;
    if (code) {
      errorEl.textContent = lookupErrorMessage(code);
      input.classList.add('chatyx-lead-form__input--error');
      input.setAttribute('aria-invalid', 'true');
    } else {
      errorEl.textContent = '';
      input.classList.remove('chatyx-lead-form__input--error');
      input.removeAttribute('aria-invalid');
    }
  };

  const showErrors = (errors) => {
    for (const field of schema.fields) {
      setFieldError(field.name, errors[field.name] || null);
    }
  };

  const updateCounter = (field) => {
    const counter = countersByName.get(field.name);
    if (!counter) return;
    const input = inputsByName.get(field.name);
    const len = input.value.length;
    counter.textContent = `${len}/${field.maxLength}`;
    counter.classList.toggle(
      'chatyx-lead-form__char-count--warning',
      len >= Math.floor(field.maxLength * CHAR_COUNT_WARN_RATIO)
    );
  };

  schema.fields.forEach((field) => {
    const input = inputsByName.get(field.name);
    input.addEventListener('input', () => {
      persistDraft();
      // Clear error as soon as the user resumes typing.
      if (input.classList.contains('chatyx-lead-form__input--error')) {
        setFieldError(field.name, null);
      }
      if (countersByName.has(field.name)) updateCounter(field);
    });
    input.addEventListener('blur', () => {
      const code = validateSingleField(field, input.value);
      setFieldError(field.name, code);
    });
    if (countersByName.has(field.name)) updateCounter(field);
  });

  const setLoading = (loading) => {
    submitBtn.disabled =
      loading || (schema.consent && schema.consent.required && consentInput && !consentInput.checked);
    submitBtn.classList.toggle('is-loading', !!loading);
    for (const input of inputsByName.values()) {
      input.disabled = loading;
    }
    if (consentInput) consentInput.disabled = loading;
  };

  const shakeRow = (name) => {
    const row = rowsByName.get(name);
    if (!row) return;
    row.classList.remove('chatyx-lead-form__field--shake');
    // Force reflow so the animation restarts even if applied twice.
    void row.offsetWidth;
    row.classList.add('chatyx-lead-form__field--shake');
    setTimeout(() => row.classList.remove('chatyx-lead-form__field--shake'), SHAKE_DURATION_MS);
  };

  const showSuccessButton = (label) => {
    submitBtn.classList.remove('is-loading');
    submitBtn.classList.add('is-submitted');
    submitBtn.disabled = true;
    while (submitBtn.firstChild) submitBtn.removeChild(submitBtn.firstChild);
    submitBtn.appendChild(createCheckIcon());
    const span = document.createElement('span');
    span.className = 'chatyx-lead-form__submit-label';
    span.textContent = label;
    submitBtn.appendChild(span);
  };

  const swapToSuccessCard = (text) => {
    wrapper.classList.add('chatyx-lead-form--submitted');
    const card = document.createElement('div');
    card.className = 'chatyx-lead-form__success';
    card.setAttribute('role', 'status');
    card.setAttribute('aria-live', 'polite');

    const iconWrap = document.createElement('div');
    iconWrap.className = 'chatyx-lead-form__success-icon';
    iconWrap.setAttribute('aria-hidden', 'true');
    iconWrap.appendChild(createCheckIcon());
    card.appendChild(iconWrap);

    const textEl = document.createElement('div');
    textEl.className = 'chatyx-lead-form__success-text';
    textEl.textContent = text;
    card.appendChild(textEl);

    wrapper.appendChild(card);
  };

  const lock = (successText) => {
    submitBtn.disabled = true;
    for (const input of inputsByName.values()) input.disabled = true;
    if (consentInput) consentInput.disabled = true;
    showSuccessButton(successText || t.leadFormSent || t.leadFormSubmitted || 'Sent');
  };

  wrapper.addEventListener('submit', async (event) => {
    event.preventDefault();
    if (honeypot.value) {
      // Silent drop: pretend success without notifying anything.
      const successMsg = t.leadFormSuccess || 'Thanks!';
      lock(t.leadFormSent || successMsg);
      status.textContent = '';
      StorageHelper.remove(draftKey);
      setTimeout(() => swapToSuccessCard(successMsg), SUCCESS_CARD_DELAY_MS);
      return;
    }
    if (schema.consent && schema.consent.required && consentInput && !consentInput.checked) {
      status.textContent = t.leadFormConsentRequired || 'Please accept to continue';
      return;
    }

    const values = {};
    for (const [name, input] of inputsByName) {
      values[name] = (input.value || '').trim();
    }
    const { valid, errors } = validateValues(schema, values);
    showErrors(errors);
    if (!valid) {
      const firstInvalid = schema.fields.find((f) => errors[f.name]);
      if (firstInvalid) {
        shakeRow(firstInvalid.name);
        const input = inputsByName.get(firstInvalid.name);
        if (input) input.focus();
      }
      return;
    }

    setLoading(true);
    status.textContent = t.leadFormSending || 'Sending…';
    try {
      const result = await onSubmit({
        formId: schema.id,
        version: schema.version,
        values,
        consent: consentInput ? consentInput.checked : null,
        consentLabel: schema.consent ? schema.consent.label : null,
        submittedAt: new Date().toISOString(),
        summary: buildSummary(schema, values, t)
      });
      const confirmation = (result && result.confirmation) || t.leadFormSuccess || 'Thanks!';
      lock(t.leadFormSent || t.leadFormSubmitted || 'Sent');
      StorageHelper.remove(draftKey);
      status.textContent = '';
      setTimeout(() => swapToSuccessCard(confirmation), SUCCESS_CARD_DELAY_MS);
    } catch (err) {
      setLoading(false);
      console.error('[chatyx] lead form submit failed', err);
      status.textContent = (err && err.userMessage) || t.leadFormError || 'Submission failed, please try again';
    }
  });

  if (container) container.appendChild(wrapper);

  // Auto-focus the first input on next frame so the chat bubble has settled.
  const firstInput = inputsByName.values().next().value;
  if (firstInput && typeof firstInput.focus === 'function') {
    requestAnimationFrame(() => {
      try {
        firstInput.focus({ preventScroll: true });
      } catch (_) {
        try {
          firstInput.focus();
        } catch (_) {
          /* noop */
        }
      }
    });
  }

  return {
    element: wrapper,
    schema,
    summary: () => {
      const values = {};
      for (const [name, input] of inputsByName) values[name] = (input.value || '').trim();
      return buildSummary(schema, values, t);
    },
    destroy: () => {
      try {
        wrapper.remove();
      } catch (_) {
        /* noop */
      }
    }
  };
}

export const __testables = {
  normalizeSchema,
  validateValues,
  validateSingleField,
  buildSummary,
  EMAIL_REGEX,
  PHONE_REGEX,
  HONEYPOT_NAME
};
